import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { LotItem } from '../../../models/inventory/lot-item';
import { IncomingShipmentType, Shipment, ShipmentStatus } from '../../../models/order/shipment';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { WarehouseProxyService } from '../../../proxies/inventory/warehouse.proxy';
import { Warehouse } from '../../../models/warehouse.ts/warehouse';
import { Invoice } from '../../../models/invoice/invoice';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import _ from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';
import { InventoryTransactionService } from '../../../services/base-actions/inventory-transaction.service';
import { InventoryTransactionManagerFactory } from '../../../services/factory/inventory-transaction.factory';
import { ApiResponse, EnumValues, GlobalEntityDefinition, EnumObjectExtended, BreadcrumbService } from '@card-software/library';
import { AuthService, SiteService } from '@card-software/nexus-utilities';
import { EnumService } from '../../../services/enum.service';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from '../../../models/translation';
import { environment } from '../../../../environments/environment';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { LocalHelperService } from '../../../services/local-helper.service';
import { InventoryTransactionType } from '../../../models/inventory/inventory-transaction';
import { ImportantLocations, SiteLocationMap } from '../../../models/location';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Business } from '../../../models/finance/business';
import { DeliveryNoteProxyService } from '../../../proxies/order/delivery-note.proxy.service';
import { DeliveryNote, DeliveryNoteReferenceLotsSearch } from '../../../models/order/delivery-note';
// import { CustomTranslateService } from '../../../services/custom.translate.loader';

@Component({
  selector: 'app-warehouse-imports-detail',
  templateUrl: './warehouse-imports-details.component.html',
  styleUrl: './warehouse-imports-details.component.scss',
})
export class WarehouseImportsDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  inventoryTransactionManager?: InventoryTransactionService;
  private loadingCounter = 0;
  translations: Record<string, Translation> = {
    Shipment_Status: {
      es: 'Estado del envío {{id}} actualizado correctamente',
      en: 'Shipment {{id}} status updated successfully',
    },
  };
  shipment?: Shipment;
  invoiceAndLots: Record<string, LotItem[]> = {};
  invoices: Invoice[] = [];
  deliveryNotes: DeliveryNote[] = [];
  lotItemLoadStatus: Record<string, boolean> = {};
  lotItemSearch: FormControl<string | null> = new FormControl(null);
  shipmentStatus: FormControl<number> = new FormControl({ value: 0, disabled: false }, { nonNullable: true });

  lotItemsToUnload: LotItem[] = [];
  client: Business | null = null;

  shipmentStatuses: EnumValues;
  shipmentStatusesIcon: EnumValues;
  shipmentStatusEnumObject?: EnumObjectExtended;

  warehouses: Warehouse[] = [];
  warehouseOptions: Warehouse[] = [];
  selectedWarehouse: FormControl<Warehouse | null> = new FormControl(null);

  // QR Scanner properties
  uniqueScanned: string[] = [];
  get isShipmentReadyToUnload(): boolean {
    return this.shipment?.status === ShipmentStatus.Unloading; // ShipmentStatus.Unloading
  }

  get isShipmentCompleted(): boolean {
    return this.shipment?.status === ShipmentStatus.Completed; // ShipmentStatus.Completed
  }

  isEditor = false;
  get isLoading(): boolean {
    return this.loadingCounter > 0;
  }

  private readonly breadcrumbService = inject(BreadcrumbService);
  constructor(
    private invoiceProxyService: InvoiceProxyService,
    private route: ActivatedRoute,
    private shipmentProxyService: ShipmentProxyService,
    private lotItemProxyService: LotItemProxyService,
    private warehouseService: WarehouseProxyService,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly inventoryTransactionManagerFactory: InventoryTransactionManagerFactory,
    private readonly enumService: EnumService,
    private readonly confirmationService: ConfirmationService,
    private translateService: TranslateService,
    private readonly userAuthenticationService: UserAuthenticationService,
    public localHelperService: LocalHelperService,
    private readonly businessProxyService: BusinessProxyService,
    private readonly deliveryNoteProxyService: DeliveryNoteProxyService,
  ) {
    this.inventoryTransactionManager = this.inventoryTransactionManagerFactory.createManager('warehouse-import');
    this.shipmentStatusEnumObject = this.enumService.getEnum('ShipmentStatus', 'order');
    this.shipmentStatuses = this.shipmentStatusEnumObject?.values ?? {};
    this.shipmentStatusesIcon = this.shipmentStatusEnumObject?.valuesEnglish ?? {};
    console.log(this.shipmentStatusEnumObject?.values);
    this.updateBreadcrumbs();
    this.isEditor = AuthService.hasRole(UserGroup.YarnWarehouseImportsAdmin);

    if (this.isEditor) {
      this.shipmentStatus.enable();
    }
  }

  private updateBreadcrumbs(shipmentNumber?: string): void {
    // Only NT keeps the legacy "Yarn Warehouse Imports" label; every other site shows the generic title.
    const NORTHERN_TEXTILES_SITE_ID = '698d23639742c79b2f8cb1f1';
    const listKey =
      SiteService.getSelectedSiteId() === NORTHERN_TEXTILES_SITE_ID ? 'Warehouse_Imports_Title' : 'Warehouse_Imports_Title_Generic';
    // set() overrides setCurrent(), so the shipment number has to go in this trail. Until it loads,
    // show only the parent crumb (same as awaitCurrent on other detail pages).
    const listCrumb = { label: listKey, route: '/warehouse-imports' };
    this.breadcrumbService.set(shipmentNumber ? [listCrumb, { label: shipmentNumber }] : [listCrumb]);
  }

  canEdit() {
    // if Admin or Editor
    if (
      AuthService.hasRole(UserGroup.YarnWarehouseImportsEditor) ||
      AuthService.hasRole(UserGroup.YarnWarehouseImportsSupervisor) ||
      AuthService.hasRole(UserGroup.YarnWarehouseImportsAdmin)
    ) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.YarnWarehouseImportsViewer)) {
      return false;
    }
    // Default
    return false;
  }

  get isConsignment(): boolean {
    return this.shipment?.type === IncomingShipmentType.Consignment;
  }

  ngOnInit(): void {
    this.shipmentStatus.valueChanges.subscribe((status) => {
      console.log('Shipment status changed:', status);
      if (!_.isNil(this.shipment) && !_.isEqual(this.shipment?.status, status)) {
        let confirmationMessage = this.translateService.instant('Warehouse_Imports_Update_Status_Confirmation_Message');

        if (status === 6) {
          confirmationMessage += this.translateService.instant('Warehouse_Imports_Update_Status_Complete_Confirmation_Message');
          const openInvTransaction = this.inventoryTransactionManager!.getOpenInventoryTransaction();
          if (!openInvTransaction) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Warning',
              detail: 'There is no open inventory transaction to close.',
            });
            this.shipmentStatus.setValue(this.shipment!.status, { emitEvent: false });
            return;
          }
        }

        this.confirmationService.confirm({
          key: 'main',
          message: confirmationMessage,
          acceptLabel: this.translateService.instant('Common_Yes'),
          accept: () => {
            const command = {
              newStatus: status,
            };
            this.loadingCounter++;
            this.shipmentProxyService.updateShipmentStatus(command, this.shipment!.id!).subscribe({
              next: (result) => {
                this.loadingCounter--;
                this.shipment!.status = status;
                this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);

                if (result.success) {
                  this.messageService.add({
                    severity: 'success',
                    summary: this.translateService.instant('Common_Success'),
                    detail: this.translations['Shipment_Status'][this.translateService.currentLang as keyof Translation].replace(
                      '{{id}}',
                      this.shipment!.shipmentNumber!,
                    ),
                  });
                }
              },
              error: () => {
                this.loadingCounter--;
                this.shipmentStatus.setValue(this.shipment!.status, { emitEvent: false });
              },
            });
          },
          reject: () => {
            this.shipmentStatus.setValue(this.shipment!.status, { emitEvent: false });
          },
        });
      }
    });

    this.getShipment();
  }

  //#region Inventory transactions Service

  get inventoryTransactions() {
    return this.inventoryTransactionManager?.inventoryTransactions();
  }

  //#endregion

  redirectToInvoiceDetails(invoiceId: string): void {
    const url = `${environment.domainUrl.order}/invoice-detail?id=${invoiceId}&token=${this.userAuthenticationService.getStoredJwtToken()}`;
    window.open(url, '_blank');
  }

  redirectToDeliveryNoteDetails(deliveryNoteId: string): void {
    const url = `${environment.domainUrl.order}/delivery-note-detail?id=${deliveryNoteId}&token=${this.userAuthenticationService.getStoredJwtToken()}`;
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveLocationCodes(): { inRoute: string; rawMaterial: string } {
    const siteId = SiteService.getSelectedSiteId();
    const mapped = siteId ? SiteLocationMap[siteId] : undefined;
    return mapped ?? { inRoute: ImportantLocations.IN_ROUTE, rawMaterial: ImportantLocations.RAW_MATERIAL };
  }

  // #region Typeaheads

  public warehouseTypeaheadSearch(event: AutoCompleteCompleteEvent): void {
    const term = event.query.toLowerCase();
    if (term === '') {
      this.warehouseOptions = this.warehouses;
    }
    this.warehouseOptions = _.sortBy(
      this.warehouses.filter((warehouse) => warehouse.code.toLowerCase().includes(term)),
      (warehouse) => warehouse.code.toLowerCase().indexOf(term),
    );
    // do a regex expression again
  }
  // #endregion

  // #region Getters
  public get shipmentStatusOptions(): EnumValues {
    if (!this.shipmentStatuses || typeof this.shipmentStatuses !== 'object') return {};

    if (AuthService.hasRole(UserGroup.YarnWarehouseImportsAdmin)) {
      const allowedValues = [3, 4, 5, 6];
      const filtered: EnumValues = {};

      for (const key of allowedValues) {
        if (this.shipmentStatuses[key]) {
          filtered[key] = this.shipmentStatuses[key];
        }
      }

      return filtered;
    }

    return {};
  }
  public getInvoice(invoiceReference: string): Invoice | undefined {
    return this.invoices.find((invoice) => invoice.id === invoiceReference);
  }

  public getAllLotItems(): LotItem[] {
    return this.isConsignment ? this.lotItemsToUnload : Object.values(this.invoiceAndLots).flat();
  }

  public getRecordsKeysWithLots(): string[] {
    //get keys where value has length > 0
    return Object.keys(this.invoiceAndLots).filter((key) => this.invoiceAndLots[key].length > 0);
  }

  public getLots(invoiceReference: string): LotItem[] {
    if (!_.isNil(this.lotItemSearch.value) && !_.isEmpty(this.lotItemSearch.value)) {
      const lotItems = this.invoiceAndLots[invoiceReference] || [];
      //search by lotItem, and providerLabelNumber. providerLabelNumber could be null
      return lotItems.filter(
        (lotItem) =>
          lotItem.number?.toLowerCase().includes(this.lotItemSearch.value!.toLocaleLowerCase()) ||
          (lotItem.providerLabelNumber &&
            lotItem.providerLabelNumber.toLowerCase().includes(this.lotItemSearch.value!.toLowerCase().replace(' ', ''))),
      );
    }
    return this.invoiceAndLots[invoiceReference] || [];
  }

  public getLotsProgress(invoiceReference: string): number {
    const lots = this.invoiceAndLots[invoiceReference] || [];
    const receivedLots = lots.filter((lot) => lot.warehouseReference);
    // round to integer not to show decimals
    return Math.round((receivedLots.length / lots.length) * 100);
  }

  // #endregion

  // #region Initializers

  private getShipment(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['id'] && params['id'] !== 'new') {
        this.loadingCounter++;
        this.shipmentProxyService
          .getShipment(params['id'])
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (response) => {
              const shipment = response.data;
              if (shipment) {
                this.shipmentStatus.setValue(shipment.status!);
                //
                this.inventoryTransactionManager?.setInitiatorEntityKey({
                  reference: shipment.id!,
                  entityDefinitionCode: GlobalEntityDefinition.Shipment,
                });
                this.inventoryTransactionManager?.setGoingToCode(ImportantLocations.KNITTING);
                this.shipment = shipment;
                this.updateBreadcrumbs(shipment.shipmentNumber);

                if (this.isConsignment) {
                  this.getConsignmentData();
                } else {
                  this.loadInvoices();
                }
                this.loadingCounter--;
              }
            },
            () => this.loadingCounter--,
          );
      } else {
        this.router.navigate(['/warehouse-imports']);
      }
    });
  }

  private getConsignmentData(): void {
    if (_.isNil(this.shipment)) {
      return;
    }

    const deliveryNoteReferences = this.shipment.deliveryNoteReferences;
    if (_.isNil(deliveryNoteReferences) || _.isEmpty(deliveryNoteReferences)) {
      return;
    }

    const deliveryNoteSearch: DeliveryNoteReferenceLotsSearch = {
      deliveryNoteReferencesIds: deliveryNoteReferences,
    };

    const lotItemTask = this.lotItemProxyService.getLotsByDeliveryNoteIds(deliveryNoteSearch);

    // Load client info if clientReference exists
    const clientReference = this.shipment?.clientReference;
    const clientTask = !_.isNil(clientReference)
      ? this.businessProxyService.getById(clientReference).pipe(
          map((response) => response.data),
          catchError(() => of(null as Business | null)),
        )
      : of(null as Business | null);

    // Load delivery notes if they exist
    const deliveryNotesTask =
      !_.isNil(deliveryNoteReferences) && !_.isEmpty(deliveryNoteReferences)
        ? this.deliveryNoteProxyService.getDeliveryNotesByIds(deliveryNoteReferences).pipe(
            map((response) => response.data || []),
            catchError(() => of([] as DeliveryNote[])),
          )
        : of([] as DeliveryNote[]);

    this.loadingCounter++;
    forkJoin([clientTask, lotItemTask, deliveryNotesTask])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([clientData, lotItemsData, deliveryNotesData]) => {
          this.client = clientData;
          this.lotItemsToUnload = lotItemsData.data || [];
          this.deliveryNotes = deliveryNotesData;

          const { inRoute, rawMaterial } = this.resolveLocationCodes();
          this.inventoryTransactionManager?.setComingFromCode(inRoute);
          this.inventoryTransactionManager?.setGoingToCode(rawMaterial);

          this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);
          this.loadingCounter--;
        },
        error: (error) => {
          console.error('Error fetching consignment data:', error);
          this.loadingCounter--;
        },
      });
  }

  private loadInvoices(): void {
    if (!_.isNil(this.shipment?.invoiceReferences) && !_.isEmpty(this.shipment?.invoiceReferences)) {
      this.loadingCounter++;
      const invoicesObservables = this.shipment.invoiceReferences.map((invoiceId) =>
        this.invoiceProxyService.getInvoice(invoiceId).pipe(map((response: ApiResponse<Invoice>) => response.data)),
      );

      forkJoin(invoicesObservables)
        .pipe(
          takeUntil(this.destroy$),
          map((invoicesOrNull: (Invoice | null)[]) => invoicesOrNull.filter((invoice): invoice is Invoice => !!invoice)),
        )
        .subscribe({
          next: (invoices: Invoice[]) => {
            this.invoices = invoices;
            const { inRoute, rawMaterial } = this.resolveLocationCodes();
            this.inventoryTransactionManager?.setComingFromCode(inRoute);
            this.inventoryTransactionManager?.setGoingToCode(rawMaterial);
            this.loadingCounter--;
            this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);
            this.loadLotItems();
          },
          error: () => {
            this.loadingCounter--;
          },
        });
    }
  }

  private loadLotItems(): void {
    this.loadingCounter++;
    const invoiceLotRequestObservables = this.invoices.map((invoice) =>
      this.lotItemProxyService.getLotsByInvoiceId(invoice.id!, false).pipe(
        map((response: ApiResponse<LotItem[] | null>) => response.data),
        catchError(() => of(null)), // Manejar errores de peticiones individuales devolviendo null
      ),
    );

    forkJoin(invoiceLotRequestObservables)
      .pipe(
        takeUntil(this.destroy$),
        map((arrayOfLotsOrNull: (LotItem[] | null)[]) =>
          arrayOfLotsOrNull.filter((lots): lots is LotItem[] => !!lots).reduce((acc, lots) => [...acc, ...lots], []),
        ),
      )
      .subscribe({
        next: (allLots: LotItem[]) => {
          this.loadingCounter--;
          this.invoiceAndLots = this.invoices.reduce(
            (acc, invoice) => {
              // Encuentra los LotItems correspondientes a esta factura in el array aplanado
              const lotsForInvoice = allLots.filter((lot) => lot.invoiceReference === invoice.id);
              acc[invoice.id!] = lotsForInvoice;
              return acc;
            },
            {} as Record<string, LotItem[]>,
          );
        },
        error: () => {
          this.showMessageModal('There was an error loading the lots');
          this.loadingCounter--;
        },
      });
  }

  // #endregion

  // #region Inventory transaction actions

  // #endregion

  //#region Actions
  receiveLotItem(lotItem: LotItem): void {
    if (_.isNil(this.selectedWarehouse.value)) {
      this.showMessageModal('Please select a warehouse');
      return;
    }
    const record = this.invoiceAndLots[lotItem.invoiceReference];
    const lot = record.find((l) => l.id === lotItem.id);
    if (lot) {
      this.lotItemLoadStatus[lotItem.id!] = true;
      this.lotItemProxyService.receiveLotItem(lotItem.number!, this.selectedWarehouse.value.id).subscribe({
        next: () => {
          this.lotItemLoadStatus[lotItem.id!] = false;
          lot.warehouseReference = this.selectedWarehouse.value!.code!;
          // lot.warehouse = this.selectedWarehouse.value!;
          lot.received = true;
          this.getLotItemLabel(lotItem);
        },
        error: () => {
          this.lotItemLoadStatus[lotItem.id!] = false;
        },
      });
    }
  }

  public getLotItemLabel(lotItem: LotItem): void {
    this.lotItemProxyService.getLotItemLabel(lotItem.id!).subscribe({
      next: (blob) => {
        // Create an object URL from the blob
        const url = window.URL.createObjectURL(blob);
        // Open the image in a new tab
        window.open(url, '_blank');
        // Optionally, you can revoke the object URL after a delay:
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      },
    });
  }

  // #endregion

  // #region Message modal
  public showMessageModal(message: string): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Info',
      detail: message,
    });
  }

  // #region QR Scanner methods
  onQrCodeScanned(code: string): void {
    console.log('QR Code scanned:', code);
    if (!this.uniqueScanned.includes(code)) {
      this.uniqueScanned.push(code);
    }
    this.lotItemSearch.setValue(code);
  }
  // #endregion

  downloadShipmentReport() {
    this.shipmentProxyService.shipmentReport(this.shipment!.id!).subscribe({
      next: (blob) => {
        const shipmentNumber = this.shipment?.shipmentNumber || 'Shipment';
        const fileName = `${shipmentNumber}_Report.pdf`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
    });
  }
}
